import { useEffect, useState } from "react";
import DashSidebar from "@/components/DashSidebar";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Search, Download, Filter, User as UserIcon, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import QuizAnswers from "@/components/QuizAnswers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_COLORS = {
  NEW: "bg-[#9CAF88]/20 text-[#2C4C3B]",
  CONTACTED: "bg-[#E07A5F]/15 text-[#C96B52]",
  SCHEDULED: "bg-blue-100 text-blue-700",
  CONVERTED: "bg-green-100 text-green-700",
  LOST: "bg-gray-200 text-gray-600",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "all", care_type: "all", assigned: "all", zip_code: "" });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.care_type !== "all") params.care_type = filters.care_type;
      if (filters.assigned !== "all") params.assigned = filters.assigned;
      if (filters.zip_code) params.zip_code = filters.zip_code;
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const { data } = await api.get("/partners");
      setPartners(data);
    } catch {}
  };

  useEffect(() => {
    load();
    loadPartners();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  const changeStatus = async (leadId, status) => {
    try {
      await api.post(`/leads/${leadId}/status`, { status });
      toast.success("Status updated");
      load();
      if (selected?.id === leadId) {
        const { data } = await api.get(`/leads/${leadId}`);
        setSelected(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const assignPartner = async (leadId, partnerId) => {
    try {
      await api.post(`/leads/${leadId}/assign`, { partner_id: partnerId || null });
      toast.success(partnerId ? "Assigned" : "Unassigned");
      load();
      if (selected?.id === leadId) {
        const { data } = await api.get(`/leads/${leadId}`);
        setSelected(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    try {
      await api.post(`/leads/${selected.id}/notes`, { note });
      setNote("");
      const { data } = await api.get(`/leads/${selected.id}`);
      setSelected(data);
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteLead = async () => {
    if (!pendingDelete) return;
    const lead = pendingDelete;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success(`Deleted ${lead.first_name || lead.email}`);
      setPendingDelete(null);
      if (selected?.id === lead.id) setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("chc_token");
    const url = `${API_BASE}/leads/export${token ? `?_=${Date.now()}` : ""}`;
    // Use fetch and trigger download
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "leads.csv";
        a.click();
      });
  };

  const partnerName = (pid) => partners.find((p) => p.id === pid)?.name || "UNASSIGNED";

  return (
    <div className="flex min-h-screen bg-[#F7F5F0]">
      <DashSidebar role="admin" />
      <main className="flex-1 lg:ml-0 ml-64 p-8 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24]">Leads</h1>
            <p className="text-[#5C6B62] mt-1">{leads.length} total · Manage, filter, assign.</p>
          </div>
          <Button onClick={exportCsv} variant="outline" className="rounded-xl h-11 border-[#E2DFD8]" data-testid="export-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        <div className="card-soft p-5 mb-6">
          <div className="grid md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute top-3.5 left-3 text-[#5C6B62]" />
              <Input
                placeholder="Search name, email, phone…"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9 h-11 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                data-testid="filter-search"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="h-11 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {["NEW", "CONTACTED", "SCHEDULED", "CONVERTED", "LOST"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.care_type} onValueChange={(v) => setFilters({ ...filters, care_type: v })}>
              <SelectTrigger className="h-11 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]" data-testid="filter-care"><SelectValue placeholder="Care type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All care types</SelectItem>
                {["Independent Living", "Assisted Living", "Memory Care", "Other"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.assigned} onValueChange={(v) => setFilters({ ...filters, assigned: v })}>
              <SelectTrigger className="h-11 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]" data-testid="filter-assigned"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All leads</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F7F5F0] border-b border-[#E2DFD8]">
                <tr className="text-left text-xs uppercase tracking-wider text-[#5C6B62]">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Care type</th>
                  <th className="px-5 py-3 font-medium">Zip</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Partner</th>
                  <th className="px-5 py-3 font-medium">Created</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2DFD8]" data-testid="leads-table-body">
                {loading && (
                  <tr><td colSpan={7} className="px-5 py-8 text-center text-[#5C6B62]">Loading…</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-[#5C6B62]">No leads match these filters.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="hover:bg-[#F7F5F0]/60" data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-[#1A2E24]">{l.first_name}</div>
                      <div className="text-xs text-[#5C6B62]">{l.email}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-[#1A2E24]">{l.care_type || "—"}</td>
                    <td className="px-5 py-4 text-sm text-[#5C6B62]">{l.zip_code || "—"}</td>
                    <td className="px-5 py-4">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger className="h-9 w-[140px] rounded-lg border-[#E2DFD8] text-xs" data-testid={`status-${l.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["NEW", "CONTACTED", "SCHEDULED", "CONVERTED", "LOST"].map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4">
                      <Select value={l.assigned_partner_id || "none"} onValueChange={(v) => assignPartner(l.id, v === "none" ? null : v)}>
                        <SelectTrigger className="h-9 w-[160px] rounded-lg border-[#E2DFD8] text-xs" data-testid={`assign-${l.id}`}>
                          <SelectValue>{partnerName(l.assigned_partner_id)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">UNASSIGNED</SelectItem>
                          {partners.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4 text-xs text-[#5C6B62]">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => setSelected(l)}
                          className="text-sm text-[#2C4C3B] font-medium hover:underline"
                          data-testid={`view-${l.id}`}
                        >
                          View
                        </button>
                        <button
                          onClick={() => setPendingDelete(l)}
                          className="text-[#C96B52] hover:text-[#A0432F] transition"
                          aria-label={`Delete ${l.first_name || l.email}`}
                          title="Delete lead"
                          data-testid={`delete-${l.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl text-[#1A2E24]">
                    {selected.first_name} <span className="text-sm text-[#5C6B62] font-normal">({selected.email})</span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-5 mt-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-[#5C6B62]">Phone:</span> <span className="text-[#1A2E24]">{selected.phone || "—"}</span></div>
                    <div><span className="text-[#5C6B62]">Zip:</span> <span className="text-[#1A2E24]">{selected.zip_code || "—"}</span></div>
                    <div><span className="text-[#5C6B62]">Care type:</span> <span className="text-[#1A2E24]">{selected.care_type || "—"}</span></div>
                    <div><span className="text-[#5C6B62]">Source:</span> <span className="text-[#1A2E24]">{selected.source}</span></div>
                    <div><span className="text-[#5C6B62]">Status:</span> <Badge className={STATUS_COLORS[selected.status]}>{selected.status}</Badge></div>
                    <div><span className="text-[#5C6B62]">SMS opt-in:</span> <span className="text-[#1A2E24]">{selected.opted_in_sms ? "Yes" : "No"}</span></div>
                  </div>

                  <div>
                    <h4 className="font-display text-lg font-semibold text-[#1A2E24] mb-3">Quiz answers</h4>
                    <QuizAnswers answers={selected.answers} careType={selected.care_type} />
                  </div>

                  <div>
                    <h4 className="font-display text-lg font-semibold text-[#1A2E24] mb-2">Notes</h4>
                    <div className="space-y-2 mb-3">
                      {(selected.notes || []).map((n) => (
                        <div key={n.id} className="bg-[#F7F5F0] rounded-xl p-3 text-sm">
                          <div className="text-[#1A2E24]">{n.text}</div>
                          <div className="text-xs text-[#5C6B62] mt-1">{n.by} · {new Date(n.at).toLocaleString()}</div>
                        </div>
                      ))}
                      {(selected.notes || []).length === 0 && <div className="text-sm text-[#5C6B62]">No notes yet.</div>}
                    </div>
                    <div className="flex gap-2">
                      <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a note…"
                        className="rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                        data-testid="lead-note-input"
                      />
                      <Button onClick={addNote} className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl self-end" data-testid="lead-note-submit">Add</Button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-display text-lg font-semibold text-[#1A2E24] mb-2">Activity</h4>
                    <div className="space-y-1.5 text-xs text-[#5C6B62] max-h-40 overflow-y-auto thin-scroll">
                      {(selected.activity || []).slice().reverse().map((a, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9CAF88] flex-shrink-0" />
                          <div>
                            <span className="text-[#1A2E24] font-medium">{a.type}</span>
                            {a.status && <> → {a.status}</>}
                            {a.care_type && <> → {a.care_type}</>}
                            <span className="ml-2 text-[#5C6B62]">{new Date(a.at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
          <AlertDialogContent data-testid="delete-lead-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes {pendingDelete?.first_name || "this lead"}
                {pendingDelete?.email ? ` (${pendingDelete.email})` : ""} along with their
                notes, activity log, and funnel events. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-lead-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteLead}
                className="bg-[#C96B52] hover:bg-[#A0432F] text-white"
                data-testid="delete-lead-confirm"
              >
                Delete lead
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
